import { ButtonWrapper } from '@/components/form-builder/sections/section/styled';
import { FF_IMMEDIATE_NURSE_CONSULTATION, FF_IMMEDIATE_NURSE_CONSULTATION_VARIANTS } from '@/constants/featureFlags';
import { GoogleAnalyticsEventName } from '@/constants/types';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useGoogleTagManager from '@/hooks/useGoogleTagManager';
import { AddDiscountCode } from '@/pages/components/AddDiscountCode';
import { formatStringToSha256Hash } from '@/utils/ga-hashing';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { Box, Button, CircularProgress, Stack, Typography, styled } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const StyledButton = styled(Button)(({ theme }) => ({
  '& .MuiTypography-root': {
    color: theme.palette.common.white
  },
  '& .MuiSvgIcon-root': {
    color: theme.palette.common.white
  }
}));

const LoadingContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '200px'
}));

export enum ImmediateConsultExperiment {
  CONTROL = 'control',
  VARIANT = 'variant',
  VARIANT_1 = 'variant-1',
  VARIANT_2 = 'variant-2'
}

export const useImmediateConsultVariant = () => {
  const { flags } = useFeatureFlags();
  return useMemo(() => {
    const flagValue = flags[FF_IMMEDIATE_NURSE_CONSULTATION_VARIANTS]?.toLowerCase();
    return Object.values(ImmediateConsultExperiment).includes(flagValue)
      ? (flagValue as ImmediateConsultExperiment)
      : ImmediateConsultExperiment.CONTROL;
  }, [flags]);
};

export const EligibilityVariantImmediateConsult = ({
  onNextStep,
  handleShowCalendar
}: {
  onNextStep: () => void;
  handleShowCalendar: () => void;
}) => {
  const { flags, loading } = useFeatureFlags();
  const [immediateConsultationAutoToggle, _] = useState(() => flags[FF_IMMEDIATE_NURSE_CONSULTATION]);
  const immediateConsultVariant = useImmediateConsultVariant();

  const { sendGoogleAnalyticsEvent } = useGoogleTagManager();

  const [searchParams] = useSearchParams();
  const promocode = searchParams.get('promocode') || '';

  const userEmail = searchParams.get('email') || localStorage.getItem('prescreeningUserEmail');
  const shaEmail = userEmail ? formatStringToSha256Hash(userEmail) : '';

  const handleAnalyticsOnClick = () => {
    const trackingPayload = {
      event_category: 'cal.com',
      event_label: 'Immediate Nurse Consultation',
      sha_email: shaEmail,
      ...(promocode ? { promocode } : {})
    };
    sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.START_BOOKING, trackingPayload);
  };

  useEffect(() => {
    // GA4 experiment impression
    if (immediateConsultationAutoToggle && !loading) {
      const trackingPayload = {
        experiment_id: FF_IMMEDIATE_NURSE_CONSULTATION,
        variant_id: immediateConsultVariant,
        experiment_platform: 'LaunchDarkly',
        sha_email: shaEmail,
        ...(promocode ? { promocode } : {})
      };
      sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.EXPERIMENT_IMPRESSION, trackingPayload);
    }
  }, [sendGoogleAnalyticsEvent, searchParams, immediateConsultVariant, shaEmail]);

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress color="primary" />
      </LoadingContainer>
    );
  }

  return (
    <Box data-testid="eligibility-variant">
      <Typography variant="h6" component="h2" fontWeight={'bold'} marginBottom={8}>
        {"You're eligible for a complimentary initial assessment"}
      </Typography>
      <Typography marginBottom={5}>Speak to one of our nurses about your medical history and our services</Typography>
      <Typography marginBottom={4}>{"What you'll need:"}</Typography>
      <Stack flexDirection={'row'} alignItems={'center'}>
        <CreditCardIcon />
        <Typography marginLeft={2}>Medicare card number (or IHI)</Typography>
      </Stack>

      <AddDiscountCode />

      <ButtonWrapper alignItems={{ md: 'center' }}>
        {immediateConsultationAutoToggle && immediateConsultVariant !== ImmediateConsultExperiment.CONTROL ? (
          <>
            <Button
              type="button"
              color="primary"
              size="large"
              variant="contained"
              onClick={() => {
                handleAnalyticsOnClick();
                onNextStep();
              }}
            >
              Proceed to consultation
            </Button>
            {[ImmediateConsultExperiment.VARIANT, ImmediateConsultExperiment.VARIANT_2].includes(
              immediateConsultVariant
            ) && (
              <>
                <Typography textAlign={'center'}>or</Typography>
                <StyledButton
                  type="button"
                  color="secondary"
                  size="large"
                  variant="contained"
                  onClick={handleShowCalendar}
                  startIcon={<CalendarTodayIcon />}
                >
                  <Typography>Schedule for later</Typography>
                </StyledButton>
              </>
            )}
          </>
        ) : (
          <StyledButton
            type="button"
            color="secondary"
            size="large"
            variant="contained"
            onClick={handleShowCalendar}
            startIcon={<CalendarTodayIcon />}
          >
            <Typography>Schedule</Typography>
          </StyledButton>
        )}
      </ButtonWrapper>
    </Box>
  );
};
